import {useEffect, useState} from 'react';
import {useForm, Controller} from "react-hook-form";
import apiClient from "../../../../utils/apiClient";
import FormSelect from "../../../forms/form-control/FormSelect";
import FormSubmitButton from "../../../forms/form-control/FormSubmitButton";
import {updateStateValue} from "../../../../utils/stateHelpers";
import {isEmpty} from "lodash";
import FormInputDate from "../../../forms/form-control/FormInputDate";
import moment from "moment";
import Creatable from 'react-select/creatable';

/**
 * Filter component for downtime by rig comparison.
 * @returns {JSX.Element}
 * @constructor
 */
const DowntimeByRigComparisonFilters = (prop) => {
    const {register, handleSubmit, errors, setValue, getValues, clearErrors, control, watch} = useForm();
    const [disciplines, setDisciplines] = useState([]);
    const [rigs, setRigs] = useState([]);
    const [financialPeriods, setFinancialPeriods] = useState([]);
    const {data, setData, setFilters} = prop;
    const [endDate, setEndDate] = useState(0);
    const rig_ids = watch('rig_ids');
    const disciplineId = watch('discipline_id');
    const dateFrom = watch('date_from');
    const dateTo = watch('date_to');

    // submit handler for report filter
    const onSubmit = (data) => {
        updateStateValue(setFilters, 'isLoading', true);
        updateStateValue(setFilters, 'isSubmitted', true);

        apiClient
            .post('getDowntimeByRigComparison', { 
                ...data,
                rig_ids: data.rig_ids.map(item => item.value),
            })
            .then(response => {
                if (isEmpty(response.data)) {
                    setData([]);
                    return;
                }
                setData(response.data);
            }).then(function () {
            updateStateValue(setFilters, 'isLoading', false);
        });
    }

    // get list of dates
    useEffect(() => {
        apiClient
            .get('getCurrentPayPeriod')
            .then(response => {
                let year = Number(moment(response.data.end_date, 'MM-DD-YYYY').format('YYYY'));
                setEndDate(year);
            });

        // get Disciplines
        apiClient
            .get('getDisciplines')
            .then(response => {
                setDisciplines(
                    response.data.disciplines.map(
                        ({id, name}) => ({id: id, name: name})
                    )
                );
        });

        // get FinancialPeriods
        apiClient
            .get('getFinancialPeriods')
            .then(response => {
                setFinancialPeriods(
                    response.data.map(
                        ({id, name, is_current}) => ({
                            id: id, name: name + (is_current ? ' (Current)' : '')
                        })
                    )
                );
            });
        
        // set yesterday date for date range components
        setValue('date_from', moment().format("YYYY-MM-DD"));
        setValue('date_to', moment().add(2, "month").format("YYYY-MM-DD"));
    }, [setValue, endDate]);

        // rigs change handler
    useEffect(() => {
        if (rig_ids && rig_ids.length === 0) {
            setValue('rig_ids', null);
        }
    }, [rig_ids, setValue]);

    useEffect(() => {
        setValue('rig_ids', null);
        setRigs([]);

        if (!isEmpty(disciplineId) && !isEmpty(dateFrom) && !isEmpty(dateTo)) {
            apiClient
                .post('getRigsByDisciplineAndDates', {
                    discipline_id: disciplineId,
                    date_from: dateFrom,
                    date_to: dateTo,
                })
                .then(response => {
                    setRigs(
                        response.data.rigs.map(
                            ({id, name}) => ({value: id, label: name})
                        )
                    );
                });
        }
    }, [disciplineId, dateFrom, dateTo, setValue])

    const setDateByMonthlyPeriod = (value) => {
        if (value) {
            apiClient
                .post('getFinancialPeriodById', {'id': value})
                .then(response => {
                    let startDate = response.data.start_date;
                    let endDate = response.data.end_date;
                    document.getElementById('date_from').value = startDate;
                    document.getElementById('date_to').value = endDate;
                    clearErrors('date_from');
                    clearErrors('date_to');
            });
        }
    }

    return (
        <form
            className="needs-validation s-filters-form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="s-filters">
                <FormSelect
                    id="discipline_id"
                    label="Discipline"
                    className="s-filter"
                    options={disciplines}
                    register={register({required: "required"})}
                    errors={errors}
                />
                <div className='col-md-3'>
                    <label htmlFor="rig_ids">Rigs</label>
                    <Controller
                        label="Rigs"
                        as={Creatable}
                        options={rigs}
                        name="rig_ids"
                        isMulti
                        control={control}
                        styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                        rules={{
                            required: "required",
                        }}
                    />
                    {errors.rig_ids && <span className="font-danger">{ errors.rig_ids.message }</span>}
                </div>
                <FormSelect
                    id="financial_period_id"
                    label="Monthly Data Period"
                    className="s-filter"
                    options={financialPeriods}
                    register={register}
                    onChange={(e) => {
                        setValue('pay_period_id', '');
                        setDateByMonthlyPeriod(e.target.value);
                        setValue('date', '');
                    }}
                    errors={errors}
                />
                <FormInputDate
                    id="date_from"
                    label="From"
                    className="s-filter"
                    onChange={() => {
                        setValue('pay_period_id', '');
                        setValue('financial_period_id', '');
                        setValue('date', '');
                        if (isEmpty(getValues('date_to'))) {
                            setValue('date_to', getValues('date_from'));
                        }

                    }}
                    register={register({required: "required"})}
                    errors={errors}
                />
                <FormInputDate
                    id="date_to"
                    label="To"
                    className="s-filter"
                    onChange={() => {
                        setValue('pay_period_id', '');
                        setValue('financial_period_id', '');
                        setValue('date', '');
                    }}
                    register={register({required: "required"})}
                    errors={errors}
                />
                
                <FormSubmitButton
                    label="Show"
                    className="s-button s-button-xl"
                />
                
                {/* For mobile view */}
                {!isEmpty(data) ? (
                    <div className="s-buttons s-justify-between">
                        <FormSubmitButton
                            label="Show"
                            className="s-button"
                        />
                    </div>
                ) : (
                    <div className="s-buttons s-justify-end">
                        <FormSubmitButton
                            label="Show"
                            className="s-button"
                        />
                    </div>
                )}
            </div>
        </form>
    );
}

export default DowntimeByRigComparisonFilters;
